import { useState, useCallback, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Table,
} from "reactstrap";
import { Oval } from "react-loader-spinner";
import {
  getGroupPostsSummary,
  getDailyPostsSummary,
  getGroupPostTrends,
  getGroupCreationSummary,
  getMemberTrendsSummary,
} from "../../http/http-calls";
import { showToast } from "../../helper-methods";
import moment from "moment";

const ReportsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Ref to track if a generation is in progress to prevent multiple simultaneous generations
  const isGeneratingRef = useRef(false);
  // State to track generation for UI updates (since refs don't trigger re-renders)
  const [isGenerating, setIsGenerating] = useState(false);

  // Special rendering functions for specific columns
  const renderTrendValue = (trendValue) => {
    if (
      !trendValue ||
      trendValue === "0%" ||
      trendValue === "0.0%" ||
      parseFloat(trendValue) === 0
    ) {
      return (
        <span className="text-muted">
          <i className="fas fa-minus me-1"></i>
          0.0%
        </span>
      );
    }

    const isPositive = trendValue.startsWith("+");
    const colorClass = isPositive ? "text-success" : "text-danger";
    const iconClass = isPositive ? "fas fa-arrow-up" : "fas fa-arrow-down";

    return (
      <span className={colorClass}>
        <i className={`${iconClass} me-1`}></i>
        {trendValue}
      </span>
    );
  };

  const renderReEngageValue = (reEngageValue) => {
    if (reEngageValue === true) {
      return (
        <span className="text-danger">
          <i className="fas fa-check-circle me-1"></i>
          Yes
        </span>
      );
    } else {
      return (
        <span className="text-success">
          <i className="fas fa-times-circle me-1"></i>
          No
        </span>
      );
    }
  };

  // Function to render combined month data (count + trend)
  const renderMonthData = (monthData) => {
    if (!monthData || typeof monthData !== "object") {
      return String(monthData || 0);
    }

    const { count, trend, isFirstMonth } = monthData;

    // For January (first month), show count with greyed out trend
    if (isFirstMonth) {
      return (
        <span className="text-muted">
          <i className="fas fa-minus me-1"></i>
          {count} (0.0%)
        </span>
      );
    }

    // For other months, show count with colored trend
    const isPositive = trend && trend.startsWith("+");
    const isZero =
      !trend ||
      trend === "0.0%" ||
      trend === "0%" ||
      trend === "+0.0%" ||
      trend === "+0%";

    const colorClass = isZero
      ? "text-muted"
      : isPositive
        ? "text-success"
        : "text-danger";
    const iconClass = isZero
      ? "fas fa-minus"
      : isPositive
        ? "fas fa-arrow-up"
        : "fas fa-arrow-down";

    return (
      <span className={colorClass}>
        <i className={`${iconClass} me-1`}></i>
        {count} ({trend || "0.0%"})
      </span>
    );
  };

  // Helper function to safely render cell values
  const renderCellValue = (value, columnKey) => {
    if (value === null || value === undefined) {
      return "N/A";
    }

    // Special handling for Group Creation report columns
    if (selectedReport === "group-creation-summary") {
      if (
        columnKey === "totalRevenue" ||
        columnKey === "averageRevenuePerGroup"
      ) {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return "$0.00";
        return (
          <span className="text-success fw-bold">
            <i className="fas fa-dollar-sign me-1"></i>
            {numValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }

      if (columnKey === "percentageWith10Plus") {
        const percentage = parseFloat(value);
        const colorClass =
          percentage >= 50
            ? "text-success"
            : percentage >= 25
              ? "text-warning"
              : "text-danger";
        const iconClass =
          percentage >= 50
            ? "fas fa-arrow-up"
            : percentage >= 25
              ? "fas fa-minus"
              : "fas fa-arrow-down";
        return (
          <span className={`${colorClass} fw-bold`}>
            <i className={`${iconClass} me-1`}></i>
            {value}
          </span>
        );
      }

      if (columnKey === "groupsWith10PlusMembers") {
        return (
          <span className="text-info fw-bold">
            <i className="fas fa-users me-1"></i>
            {value}
          </span>
        );
      }

      if (columnKey === "totalGroupsCreated") {
        return (
          <span className="text-primary fw-bold">
            <i className="fas fa-plus-circle me-1"></i>
            {value}
          </span>
        );
      }

      if (columnKey === "topRevenueGroups" && Array.isArray(value)) {
        return (
          <div className="small">
            {value.slice(0, 3).map((group, idx) => (
              <div key={idx} className="mb-1">
                <strong>{group.name}</strong>
                <br />
                <small className="text-muted">
                  {group.memberCount} members • ${group.revenue.toFixed(2)} •{" "}
                  {group.leader}
                </small>
              </div>
            ))}
          </div>
        );
      }
    }

    // Special handling for Member Trends report columns
    if (selectedReport === "member-trends-summary") {
      if (columnKey === "currentMemberCount" || columnKey === "previousMemberCount") {
        return (
          <span className="text-info fw-bold">
            <i className="fas fa-users me-1"></i>
            {value || 0}
          </span>
        );
      }

      if (columnKey === "memberChange") {
        const change = parseInt(value);
        const colorClass = change > 0 ? "text-success" : change < 0 ? "text-danger" : "text-muted";
        const iconClass = change > 0 ? "fas fa-arrow-up" : change < 0 ? "fas fa-arrow-down" : "fas fa-minus";
        const prefix = change > 0 ? "+" : "";
        
        return (
          <span className={`${colorClass} fw-bold`}>
            <i className={`${iconClass} me-1`}></i>
            {prefix}{change}
          </span>
        );
      }

      if (columnKey === "percentageChange") {
        const isPositive = value.startsWith("+");
        const isZero = value === "0.00%" || value === "+0.00%";
        const colorClass = isZero ? "text-muted" : isPositive ? "text-success" : "text-danger";
        const iconClass = isZero ? "fas fa-minus" : isPositive ? "fas fa-arrow-up" : "fas fa-arrow-down";

        return (
          <span className={`${colorClass} fw-bold`}>
            <i className={`${iconClass} me-1`}></i>
            {value}
          </span>
        );
      }

      if (columnKey === "trend") {
        const trendColors = {
          growing: "text-success",
          declining: "text-danger", 
          stable: "text-muted"
        };
        const trendIcons = {
          growing: "fas fa-chart-line",
          declining: "fas fa-chart-line-down",
          stable: "fas fa-minus"
        };
        
        return (
          <span className={`${trendColors[value] || "text-muted"} fw-bold`}>
            <i className={`${trendIcons[value] || "fas fa-minus"} me-1`}></i>
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      }

      if (columnKey === "monthsActive") {
        return (
          <span className="text-primary">
            <i className="fas fa-calendar-alt me-1"></i>
            {value} month{value !== 1 ? 's' : ''}
          </span>
        );
      }
    }

    // Special handling for month columns (combined count + trend)
    if (columnKey.includes("_") && columnKey.match(/^[A-Za-z]+_\d{4}$/)) {
      return renderMonthData(value);
    }

    // Special handling for specific columns
    if (
      columnKey === "trend" ||
      columnKey === "annualTrend" ||
      columnKey.endsWith("_Trend")
    ) {
      return renderTrendValue(value);
    }

    if (columnKey === "reEngage") {
      return renderReEngageValue(value);
    }

    // Handle boolean values
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Handle arrays - join with commas
    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            // Handle object items in arrays
            if (item._id)
              return (
                item.name ||
                item.title ||
                String(item._id).substring(0, 8) + "..."
              );
            if (item.name) return item.name;
            if (item.title) return item.title;
            return "Object";
          }
          return String(item);
        })
        .join(", ");
    }

    // Handle objects - stringify or show specific properties
    if (typeof value === "object" && value !== null) {
      // For date objects
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      // Handle MongoDB ObjectId (they have a specific toString method)
      if (
        value.constructor.name === "ObjectId" ||
        (value._bsontype && value._bsontype === "ObjectId")
      ) {
        return String(value).substring(0, 8) + "...";
      }

      // For MongoDB ObjectId or objects with specific properties
      if (value._id) {
        const displayName = value.name || value.title;
        if (displayName) {
          return `${displayName} (${String(value._id).substring(0, 8)}...)`;
        }
        return String(value._id).substring(0, 8) + "...";
      }

      // For other objects, try to display meaningfully
      if (value.name) return value.name;
      if (value.title) return value.title;
      if (value.count !== undefined) return String(value.count);
      if (value.value !== undefined) return String(value.value);

      // As last resort, show key-value pairs for small objects
      const keys = Object.keys(value);
      if (keys.length <= 2) {
        return keys
          .map((key) => {
            const val = value[key];
            if (typeof val === "object") return `${key}: [Object]`;
            return `${key}: ${val}`;
          })
          .join(", ");
      }

      // For complex objects, show a summary
      return `{${keys.length} properties}`;
    }

    // Handle numbers - format appropriately
    if (typeof value === "number") {
      // Format decimal numbers to 2 places if they have decimals
      if (value % 1 !== 0) {
        return value.toFixed(2);
      }
      return String(value);
    }

    // For strings and other primitive types - truncate if too long
    const stringValue = String(value);
    if (stringValue.length > 100) {
      return stringValue.substring(0, 97) + "...";
    }

    return stringValue;
  };

  // Helper function to format column headers
  const formatColumnHeader = (key) => {
    // Handle specific common keys
    const keyMappings = {
      _id: "ID",
      groupId: "Group ID",
      groupName: "Group Name",
      groupLeader: "Group Leader",
      totalPosts: "Total Posts",
      trend: "Monthly Trend", // Legacy support - will be overridden by Member Trends if applicable
      annualTrend: "Annual Trend",
      reEngage: "Re-engage",
      avgPostsPerMonth: "Avg Posts/Month",
      needsReengagement: "Needs Re-engagement",
      lastPostDate: "Last Post Date",
      monthsAnalyzed: "Months Analyzed",
      postsCount: "Posts Count",
      membersCount: "Members Count",
      createdAt: "Created At",
      updatedAt: "Updated At",
      // Group Creation Report specific mappings
      year: "Year",
      month: "Month #",
      monthName: "Month",
      totalGroupsCreated: "Total Groups Created",
      groupsWith10PlusMembers: "Groups with 10+ Members",
      percentageWith10Plus: "% with 10+ Members",
      averageRevenuePerGroup: "Avg Revenue per Group",
      totalRevenue: "Total Revenue",
      topRevenueGroups: "Top 3 Revenue Groups",
      // Member Trends Report specific mappings
      currentMemberCount: "Current Members",
      previousMemberCount: "Previous Members",
      memberChange: "Member Change",
      percentageChange: "% Change",
      monthYear: "Period",
      lastUpdated: "Last Updated",
      monthsActive: "Months Active",
    };

    // Handle report-specific overrides
    if (selectedReport === "member-trends-summary" && key === "trend") {
      return "Trend Status";
    }

    if (keyMappings[key]) {
      return keyMappings[key];
    }

    // Handle month trend columns (e.g., "January_Trend")
    if (key.endsWith("_Trend")) {
      const monthName = key.replace("_Trend", "");
      return `${monthName} Trend`;
    }

    // Handle month columns (e.g., "January_2024")
    if (key.includes("_") && key.match(/^[A-Za-z]+_\d{4}$/)) {
      return key.replace("_", " ");
    }

    // Default formatting: convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };
  const [filters, setFilters] = useState({
    month: moment().format("MM"), // Current month
    year: moment().format("YYYY"), // Current year
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredReportData, setFilteredReportData] = useState([]);

  // Available report types
  const reportTypes = [
    { value: "group-posts-summary", label: "Group Posts Summary by Month" },
    { value: "daily-posts-summary", label: "Daily Posts Summary by Month" },
    {
      value: "group-post-trends",
      label: "Group Post Trends (Month-over-Month)",
    },
    {
      value: "group-creation-summary",
      label: "Group Creation & Revenue Summary",
    },
    {
      value: "member-trends-summary",
      label: "Member Growth Trends (Month-over-Month)",
    },
  ];

  // Generate month options
  const monthOptions = [
    { value: "", label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Generate year options (from 2020 to current year + 1)
  const currentYear = moment().year();
  const yearOptions = [
    { value: "", label: "All Years" },
    ...Array.from({ length: currentYear - 2019 }, (_, i) => {
      const year = currentYear - i;
      return { value: year.toString(), label: year.toString() };
    }),
  ];

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    // Prevent filter changes while a report is being generated
    if (isGenerating) {
      return;
    }
    
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
    setPagination(null);
  };

  // Generate CSV filename based on report type and filters
  const getCSVFilename = () => {
    const reportType =
      reportTypes.find((r) => r.value === selectedReport)?.label || "Report";
    const timestamp = moment().format("YYYY-MM-DD");

    let filename = `${reportType.replace(/\s+/g, "_")}_${timestamp}`;

    // Add month/year to filename if specified
    if (filters.year && filters.year !== "") {
      filename += `_${filters.year}`;
      if (filters.month && filters.month !== "") {
        const monthName = moment(filters.month, "MM").format("MMMM");
        filename += `_${monthName}`;
      }
    } else if (filters.month && filters.month !== "") {
      const monthName = moment(filters.month, "MM").format("MMMM");
      filename += `_${monthName}_AllYears`;
    }

    return `${filename}.csv`;
  };

  // Generate Group Posts Summary using backend aggregation endpoint
  const groupPostsSummary = useCallback(async () => {
    try {
      // Call the new backend aggregation endpoint with pagination
      const response = await getGroupPostsSummary({
        month: filters.month || undefined,
        year: filters.year || undefined,
        pageNumber: currentPage,
        pageSize: 50,
      });

      const data = response?.data || [];
      const paginationInfo = response?.pagination || null;

      if (data.length === 0) {
        showToast("No data available for the selected filters.", "warning");
        setPagination(null);
        return [];
      }

      setPagination(paginationInfo);

      console.log(`Generated report with ${data.length} entries`);
      return data;
    } catch (error) {
      console.error("Error generating group posts summary:", error);
      throw error;
    }
  }, [filters, currentPage]);

  // Generate Daily Posts Summary using backend endpoint
  const dailyPostsSummary = useCallback(async () => {
    try {
      // Call the new backend endpoint
      const response = await getDailyPostsSummary({
        month: filters.month || undefined,
        year: filters.year || undefined,
      });

      const data = response?.data || [];

      if (data.length === 0) {
        showToast("No data available for the selected filters.", "warning");
        return [];
      }

      console.log(`Generated daily report with ${data.length} entries`);
      return data;
    } catch (error) {
      console.error("Error generating daily posts summary:", error);
      throw error;
    }
  }, [filters]);

  // Generate Group Post Trends using backend endpoint
  const groupPostTrends = useCallback(async () => {
    try {
      // Call the new backend endpoint
      const response = await getGroupPostTrends({
        year: filters.year || undefined,
        pageNumber: currentPage,
        pageSize: 50,
      });

      const data = response?.data || [];
      const paginationInfo = response?.pagination || null;

      if (data.length === 0) {
        showToast("No data available for the selected filters.", "warning");
        setPagination(null);
        return [];
      }

      setPagination(paginationInfo);
      return data;
    } catch (error) {
      console.error("Error generating group post trends:", error);
      throw error;
    }
  }, [filters, currentPage]);

  // Generate Group Creation Summary using backend endpoint
  const groupCreationSummary = useCallback(async () => {
    try {
      // Call the new backend endpoint
      const response = await getGroupCreationSummary({
        month: filters.month || undefined,
        year: filters.year || undefined,
        pageNumber: currentPage,
        pageSize: 50,
      });

      const data = response?.data || [];
      const paginationInfo = response?.pagination || null;

      if (data.length === 0) {
        showToast("No data available for the selected filters.", "warning");
        setPagination(null);
        return [];
      }

      setPagination(paginationInfo);

      console.log(
        `Generated group creation report with ${data.length} entries`,
      );
      return data;
    } catch (error) {
      console.error("Error generating group creation summary:", error);
      throw error;
    }
  }, [filters, currentPage]);

  // Generate Member Trends Summary using backend endpoint
  const memberTrendsSummary = useCallback(async () => {
    try {
      // Call the new backend endpoint
      const response = await getMemberTrendsSummary({
        month: filters.month || undefined,
        year: filters.year || undefined,
        pageNumber: currentPage,
        pageSize: 50,
      });

      const data = response?.data || [];
      const paginationInfo = response?.pagination || null;

      if (data.length === 0) {
        showToast("No data available for the selected filters.", "warning");
        setPagination(null);
        return [];
      }

      setPagination(paginationInfo);

      console.log(
        `Generated member trends report with ${data.length} entries`,
      );
      return data;
    } catch (error) {
      console.error("Error generating member trends summary:", error);
      throw error;
    }
  }, [filters, currentPage]);

  // Helper function to validate and sanitize report data
  const validateReportData = (data) => {
    if (!Array.isArray(data)) {
      console.warn("Report data is not an array:", data);
      return [];
    }

    return data.filter((item) => {
      // Ensure each item is an object
      if (typeof item !== "object" || item === null) {
        console.warn("Filtering out non-object item:", item);
        return false;
      }
      return true;
    });
  };

  // Function to fetch ALL data for CSV export (without pagination)
  const fetchAllDataForCSV = async () => {
    try {
      let allData = [];

      switch (selectedReport) {
        case "group-posts-summary":
          const summaryResponse = await getGroupPostsSummary({
            month: filters.month || undefined,
            year: filters.year || undefined,
            pageNumber: 1,
            pageSize: 10000, // Large number to get all results
          });
          allData = summaryResponse?.data || [];
          break;

        case "daily-posts-summary":
          const dailyResponse = await getDailyPostsSummary({
            month: filters.month || undefined,
            year: filters.year || undefined,
          });
          allData = dailyResponse?.data || [];
          break;

        case "group-post-trends":
          // For trends, we need to fetch all pages
          const trendsResponse = await getGroupPostTrends({
            year: filters.year || undefined,
            pageNumber: 1,
            pageSize: 10000, // Large number to get all results
          });
          allData = trendsResponse?.data || [];
          break;

        case "group-creation-summary":
          const creationResponse = await getGroupCreationSummary({
            month: filters.month || undefined,
            year: filters.year || undefined,
            pageNumber: 1,
            pageSize: 10000, // Large number to get all results
          });
          allData = creationResponse?.data || [];
          break;

        case "member-trends-summary":
          const memberTrendsResponse = await getMemberTrendsSummary({
            month: filters.month || undefined,
            year: filters.year || undefined,
            pageNumber: 1,
            pageSize: 10000, // Large number to get all results
          });
          allData = memberTrendsResponse?.data || [];
          break;

        default:
          allData = [];
      }

      return validateReportData(allData);
    } catch (error) {
      console.error("Error fetching all data for CSV:", error);
      showToast("Error preparing CSV data. Please try again.", "error");
      return [];
    }
  };

  // Handle CSV export with all data or filtered data if search is active
  const handleCSVExport = async () => {
    try {
      setIsLoading(true);

      // If there's a search term, use all search results (not just current page)
      let dataToExport;
      if (
        searchTerm &&
        window.allSearchResults &&
        window.allSearchResults.length > 0
      ) {
        dataToExport = window.allSearchResults; // Export all search results, not just current page
        showToast(
          `Exporting ${window.allSearchResults.length} filtered records...`,
          "info",
        );
      } else if (searchTerm && filteredReportData.length > 0) {
        dataToExport = filteredReportData;
        showToast(
          `Exporting ${filteredReportData.length} filtered records...`,
          "info",
        );
      } else if (!searchTerm) {
        // Fetch all data (not just current page)
        dataToExport = await fetchAllDataForCSV();
      } else {
        showToast("No data available to export.", "warning");
        return;
      }

      if (dataToExport.length === 0) {
        showToast("No data available to export.", "warning");
        return;
      }

      // Prepare CSV data
      const csvData = prepareDataForCSV(dataToExport);

      // Create CSV content
      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(","),
        ...csvData.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              // Escape quotes and wrap in quotes if contains comma
              if (
                typeof value === "string" &&
                (value.includes(",") || value.includes('"'))
              ) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(","),
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", getCSVFilename());
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      const exportMessage = searchTerm
        ? `Successfully exported ${dataToExport.length} filtered records to CSV`
        : `Successfully exported ${dataToExport.length} records to CSV`;
      showToast(exportMessage, "success");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showToast("Error exporting CSV. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Generate report data based on selected type
  const generateReport = useCallback(async () => {
    if (!selectedReport) {
      showToast("Please select a report type", "error");
      return;
    }

    // Prevent multiple simultaneous generations
    if (isGeneratingRef.current) {
      console.log("Generation already in progress, skipping...");
      return;
    }

    isGeneratingRef.current = true;
    setIsGenerating(true);
    setIsLoading(true);
    let isMounted = true;

    try {
      let data = [];

      switch (selectedReport) {
        case "group-posts-summary":
          data = await groupPostsSummary();
          break;
        case "daily-posts-summary":
          data = await dailyPostsSummary();
          break;
        case "group-post-trends":
          data = await groupPostTrends();
          break;
        case "group-creation-summary":
          data = await groupCreationSummary();
          break;
        case "member-trends-summary":
          data = await memberTrendsSummary();
          break;
        default:
          data = [];
      }

      if (isMounted) {
        const validatedData = validateReportData(data);
        setReportData(validatedData);
        if (!pagination) {
          showToast("Report generated successfully", "success");
        }
      }
    } catch (error) {
      console.error("Error generating report:", error);
      if (isMounted) {
        showToast("Error generating report. Please try again.", "error");
        // Reset report data on error to prevent rendering issues
        setReportData([]);
        setPagination(null);
      }
    } finally {
      if (isMounted) {
        setIsLoading(false);
      }
      // Reset generation flag and state regardless of mount status
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  }, [selectedReport, groupPostsSummary, dailyPostsSummary, groupPostTrends, groupCreationSummary, memberTrendsSummary, pagination]);

  // Helper function to prepare data for CSV export
  const prepareDataForCSV = (data) => {
    return data.map((row) => {
      const csvRow = {};
      Object.keys(row).forEach((key) => {
        const value = row[key];

        // Special handling for CSV export - no HTML elements
        if (selectedReport === "group-creation-summary") {
          if (key === "totalRevenue" || key === "averageRevenuePerGroup") {
            csvRow[formatColumnHeader(key)] =
              `$${parseFloat(value || 0).toFixed(2)}`;
          } else if (key === "topRevenueGroups" && Array.isArray(value)) {
            csvRow[formatColumnHeader(key)] = value
              .map(
                (g) =>
                  `${g.name} (${g.memberCount} members, $${g.revenue.toFixed(2)}, ${g.leader})`,
              )
              .join("; ");
          } else {
            csvRow[formatColumnHeader(key)] = renderCellValueForCSV(value);
          }
        } else if (selectedReport === "member-trends-summary") {
          if (key === "memberChange") {
            const change = parseInt(value);
            csvRow[formatColumnHeader(key)] = change > 0 ? `+${change}` : String(change);
          } else if (key === "trend") {
            csvRow[formatColumnHeader(key)] = value.charAt(0).toUpperCase() + value.slice(1);
          } else if (key === "monthsActive") {
            csvRow[formatColumnHeader(key)] = `${value} month${value !== 1 ? 's' : ''}`;
          } else {
            csvRow[formatColumnHeader(key)] = renderCellValueForCSV(value);
          }
        } else if (key.includes("_") && key.match(/^[A-Za-z]+_\d{4}$/)) {
          // Handle month columns - combine count and trend for CSV
          if (value && typeof value === "object" && value.count !== undefined) {
            csvRow[formatColumnHeader(key)] =
              `${value.count} (${value.trend || "0%"})`;
          } else {
            csvRow[formatColumnHeader(key)] = String(value || 0);
          }
        } else if (
          key === "trend" ||
          key === "annualTrend" ||
          key.endsWith("_Trend")
        ) {
          csvRow[formatColumnHeader(key)] = value; // Just the percentage value
        } else if (key === "reEngage") {
          csvRow[formatColumnHeader(key)] = value === true ? "true" : "false";
        } else {
          // Use a simplified version of renderCellValue for CSV
          csvRow[formatColumnHeader(key)] = renderCellValueForCSV(value);
        }
      });
      return csvRow;
    });
  };

  // Simplified cell value rendering for CSV export (no HTML)
  const renderCellValueForCSV = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            if (item.name) return item.name;
            if (item.title) return item.title;
            return "Object";
          }
          return String(item);
        })
        .join(", ");
    }

    if (typeof value === "object" && value !== null) {
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      if (value._id) {
        const displayName = value.name || value.title;
        if (displayName) {
          return `${displayName} (${String(value._id).substring(0, 8)}...)`;
        }
        return String(value._id).substring(0, 8) + "...";
      }

      if (value.name) return value.name;
      if (value.title) return value.title;

      return "{Object}";
    }

    if (typeof value === "number") {
      if (value % 1 !== 0) {
        return value.toFixed(2);
      }
    }

    return String(value);
  };

  // Filter report data based on search term
  const filterReportData = (data, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      return data;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    return data.filter((row) => {
      // Search through all string values in the row
      return Object.entries(row).some(([key, value]) => {
        // Skip searching in certain fields that don't make sense
        if (
          key === "_id" ||
          key === "totalPosts" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          return false;
        }

        // Handle different data types
        if (typeof value === "string") {
          return value.toLowerCase().includes(lowerSearchTerm);
        }

        // Handle objects (like month data)
        if (typeof value === "object" && value !== null) {
          if (value.name) {
            return value.name.toLowerCase().includes(lowerSearchTerm);
          }
          if (value.title) {
            return value.title.toLowerCase().includes(lowerSearchTerm);
          }
        }

        return false;
      });
    });
  };

  // Handle search input changes
  const handleSearchChange = async (searchValue) => {
    // Prevent search changes while a report is being generated
    if (isGenerating) {
      return;
    }
    
    setSearchTerm(searchValue);

    if (searchValue && searchValue.trim() !== "" && reportData.length > 0) {
      // If user is searching and we have paginated data, load all data for comprehensive search
      if (
        pagination &&
        (selectedReport === "group-posts-summary" ||
          selectedReport === "group-post-trends" ||
          selectedReport === "group-creation-summary" ||
          selectedReport === "member-trends-summary")
      ) {
        try {
          setIsLoading(true);
          const allData = await fetchAllDataForCSV();
          const filtered = filterReportData(allData, searchValue);
          setFilteredReportData(filtered);

          // Temporarily store original report data and pagination
          if (!window.originalReportData) {
            window.originalReportData = reportData;
            window.originalPagination = pagination;
          }

          // Store all search results for pagination
          window.allSearchResults = filtered;

          // Update display to show all data
          setReportData(allData);

          // Create pagination for search results if needed (more than 50 results)
          if (filtered.length > 50) {
            const searchPagination = {
              currentPage: 1,
              totalPages: Math.ceil(filtered.length / 50),
              totalResults: filtered.length,
              hasNextPage: filtered.length > 50,
              hasPreviousPage: false,
              isSearchPagination: true,
            };
            setPagination(searchPagination);
            // Show only first 50 results initially
            setFilteredReportData(filtered.slice(0, 50));
          } else {
            setPagination(null); // Remove pagination for small search results
          }
        } catch (error) {
          console.error("Error loading all data for search:", error);
          // Fallback to current page search
          const filtered = filterReportData(reportData, searchValue);
          setFilteredReportData(filtered);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Normal filtering for non-paginated or already loaded data
        const filtered = filterReportData(reportData, searchValue);
        setFilteredReportData(filtered);
      }
    } else if (!searchValue || searchValue.trim() === "") {
      // Restore original paginated data when search is cleared
      if (window.originalReportData && window.originalPagination) {
        setReportData(window.originalReportData);
        setPagination(window.originalPagination);
        setFilteredReportData(window.originalReportData);

        // Clean up
        delete window.originalReportData;
        delete window.originalPagination;
        delete window.allSearchResults; // Clean up search results cache
      } else {
        const filtered = filterReportData(reportData, searchValue);
        setFilteredReportData(filtered);
      }
    }
  };

  // Update filtered data when report data changes
  useEffect(() => {
    const filtered = filterReportData(reportData, searchTerm);
    setFilteredReportData(filtered);
  }, [reportData, searchTerm]);

  // Handle pagination navigation
  const handlePageChange = async (newPage) => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      // Handle search pagination
      if (
        pagination?.isSearchPagination &&
        searchTerm &&
        window.allSearchResults
      ) {
        const startIndex = (newPage - 1) * 50;
        const endIndex = startIndex + 50;
        const paginatedResults = window.allSearchResults.slice(
          startIndex,
          endIndex,
        );

        setFilteredReportData(paginatedResults);
        setPagination((prev) => ({
          ...prev,
          currentPage: newPage,
          hasNextPage: endIndex < window.allSearchResults.length,
          hasPreviousPage: newPage > 1,
        }));
      } else {
        // Normal pagination - update currentPage state first
        setCurrentPage(newPage);

        let data = [];
        if (selectedReport === "group-post-trends") {
          data = await groupPostTrends();
        } else if (selectedReport === "group-posts-summary") {
          data = await groupPostsSummary();
        } else if (selectedReport === "group-creation-summary") {
          data = await groupCreationSummary();
        } else if (selectedReport === "member-trends-summary") {
          data = await memberTrendsSummary();
        }
        const validatedData = validateReportData(data);
        setReportData(validatedData);
        setFilteredReportData(validatedData);
      }
    } catch (error) {
      console.error("Pagination error:", error);
      showToast("Error loading page", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-generate report when page changes for paginated reports
  useEffect(() => {
    if (
      selectedReport === "group-post-trends" ||
      selectedReport === "group-posts-summary" ||
      selectedReport === "group-creation-summary" ||
      selectedReport === "member-trends-summary"
    ) {
      // Only regenerate if we have data and page changed, or if it's the initial load
      if (reportData.length > 0 || currentPage === 1) {
        generateReport().catch((error) => {
          console.error("Auto-generation failed:", error);
        });
      }
    }
  }, [currentPage, selectedReport, generateReport, reportData.length]);

  return (
    <>
      <div className="innerHeader">
        <h2>Reports</h2>
      </div>

      {/* Filter section styled like GroupsPage */}
      <div className="filterWrapper mt-2">
        <div className="filterIcon">
          <i className="fas fa-filter"></i>
        </div>

        <div className="filterForm">
          <div className="formGroup">
            <Label>Report Type</Label>
            <Input
              type="select"
              value={selectedReport}
              onChange={(e) => {
                setSelectedReport(e.target.value);
                setCurrentPage(1);
                setPagination(null);
                setReportData([]);
                // Reset generation flag when changing report type
                isGeneratingRef.current = false;
                setIsGenerating(false);
              }}
              disabled={isLoading || isGenerating}
            >
              <option value="">Select Report Type</option>
              {reportTypes.map((report) => (
                <option key={report.value} value={report.value}>
                  {report.label}
                </option>
              ))}
            </Input>
          </div>

          {selectedReport !== "group-post-trends" && (
            <div className="formGroup">
              <Label>Month</Label>
              <Input
                type="select"
                value={filters.month}
                onChange={(e) => handleFilterChange("month", e.target.value)}
                disabled={isLoading || isGenerating}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </div>
          )}

          <div className="formGroup">
            <Label>Year</Label>
            <Input
              type="select"
              value={filters.year}
              onChange={(e) => handleFilterChange("year", e.target.value)}
              disabled={isLoading || isGenerating}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </div>

          <div className="formGroup">
            <Label>Generate</Label>
            <div>
              <Button
                color="primary"
                onClick={generateReport}
                disabled={isLoading || !selectedReport}
                className="w-100 d-flex flex-row justify-content-center align-items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Oval color="#ffffff" height={16} width={16} />{" "}
                    Generating...
                  </>
                ) : (
                  "Generate Report"
                )}
              </Button>
            </div>
          </div>

          {reportData.length > 0 && (
            <div className="formGroup">
              <Label>Export</Label>
              <div className="rounded-xl overflow-hidden bg-black py-2 px-4">
                <button
                  onClick={handleCSVExport}
                  className="text-white text-decoration-none border-0 bg-transparent"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-download me-2"></i>
                  {isLoading
                    ? "Preparing..."
                    : searchTerm
                      ? `Download CSV (${window.allSearchResults?.length || filteredReportData.length} filtered records)`
                      : "Download CSV (All Data)"}
                </button>
              </div>
            </div>
          )}

          {/* Search functionality for reports */}
          <div className="formGroup searchbar" style={{ minWidth: "360px" }}>
            <Label>Search Reports</Label>
            <InputGroup>
              <Input
                placeholder="Search by group name, leader, etc..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ minWidth: "250px" }}
                disabled={isLoading || isGenerating}
              />
              {searchTerm && (
                <InputGroupText
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSearchChange("")}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </InputGroupText>
              )}
              <InputGroupText>
                <i className="fas fa-search"></i>
              </InputGroupText>
            </InputGroup>
            {searchTerm && (
              <small className="text-muted mt-1">
                {pagination?.isSearchPagination ? (
                  <>
                    Showing {(pagination.currentPage - 1) * 50 + 1} to{" "}
                    {Math.min(
                      pagination.currentPage * 50,
                      pagination.totalResults,
                    )}{" "}
                    of {pagination.totalResults} records matching "{searchTerm}"
                  </>
                ) : filteredReportData.length > 0 ? (
                  `Showing ${filteredReportData.length} of ${reportData.length} records matching "${searchTerm}"`
                ) : (
                  `No records found matching "${searchTerm}"`
                )}
              </small>
            )}
          </div>
        </div>
      </div>
      {/* Preview Table */}
      {reportData.length > 0 &&
        Array.isArray(reportData) &&
        reportData[0] &&
        typeof reportData[0] === "object" && (
          <Row className="mt-4">
            <Col lg="12">
              <Card>
                <CardHeader>
                  <h5>Report Preview</h5>
                </CardHeader>
                <CardBody>
                  {(() => {
                    try {
                      if (isLoading) {
                        return (
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              margin: "40px 0",
                              minHeight: "200px",
                              alignItems: "center",
                            }}
                          >
                            <Oval color="#3498DB" />
                          </div>
                        );
                      }
                      return (
                        <div>
                          <Table responsive striped>
                            <thead>
                              <tr>
                                {Object.keys(reportData[0])
                                  .filter((key) => {
                                    // Hide internal fields for Group Creation report
                                    if (
                                      selectedReport ===
                                      "group-creation-summary"
                                    ) {
                                      return !["month", "year"].includes(key);
                                    }
                                    return true;
                                  })
                                  .map((key) => (
                                    <th key={key}>{formatColumnHeader(key)}</th>
                                  ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredReportData &&
                              filteredReportData.length > 0 ? (
                                filteredReportData.map((row, index) => {
                                  const columnKeys = Object.keys(row).filter(
                                    (key) => {
                                      // Hide internal fields for Group Creation report
                                      if (
                                        selectedReport ===
                                        "group-creation-summary"
                                      ) {
                                        return !["month", "year"].includes(key);
                                      }
                                      return true;
                                    },
                                  );
                                  return (
                                    <tr key={index}>
                                      {columnKeys.map((key, cellIndex) => (
                                        <td key={cellIndex}>
                                          {renderCellValue(row[key], key)}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })
                              ) : searchTerm ? (
                                <tr>
                                  <td
                                    colSpan={
                                      Object.keys(reportData[0]).filter(
                                        (key) => {
                                          if (
                                            selectedReport ===
                                            "group-creation-summary"
                                          ) {
                                            return !["month", "year"].includes(
                                              key,
                                            );
                                          }
                                          return true;
                                        },
                                      ).length
                                    }
                                    className="text-center text-muted p-3"
                                  >
                                    No records match your search criteria for "
                                    {searchTerm}"
                                  </td>
                                </tr>
                              ) : (
                                <tr>
                                  <td
                                    colSpan={
                                      Object.keys(reportData[0]).filter(
                                        (key) => {
                                          if (
                                            selectedReport ===
                                            "group-creation-summary"
                                          ) {
                                            return !["month", "year"].includes(
                                              key,
                                            );
                                          }
                                          return true;
                                        },
                                      ).length
                                    }
                                    className="text-center text-muted p-3"
                                  >
                                    No data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      );
                    } catch (error) {
                      console.error("Error rendering table:", error);
                      return (
                        <div className="alert alert-danger">
                          <h6>Error Rendering Table</h6>
                          <p>
                            There was an issue displaying the report data.
                            Please try regenerating the report.
                          </p>
                          <small>Error: {error.message}</small>
                        </div>
                      );
                    }
                  })()}
                  <div className="mt-2 d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      {pagination && !searchTerm ? (
                        <>
                          Showing {reportData.length} of{" "}
                          {pagination.totalResults} record(s) (Page{" "}
                          {pagination.currentPage} of {pagination.totalPages})
                        </>
                      ) : !searchTerm ? (
                        `Showing ${reportData.length} record(s)`
                      ) : null}
                    </div>

                    {/* Pagination Controls for paginated reports and search results */}
                    {pagination &&
                      (pagination.isSearchPagination ||
                        (!searchTerm &&
                          (selectedReport === "group-post-trends" ||
                            selectedReport === "group-posts-summary" ||
                            selectedReport === "group-creation-summary" ||
                            selectedReport === "member-trends-summary"))) && (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="secondary"
                            disabled={!pagination.hasPreviousPage || isLoading}
                            onClick={() =>
                              handlePageChange(pagination.currentPage - 1)
                            }
                          >
                            Previous
                          </Button>
                          <span className="px-3 py-1 bg-light d-flex align-items-center rounded text-muted">
                            {pagination.currentPage} / {pagination.totalPages}
                          </span>
                          <Button
                            size="sm"
                            color="secondary"
                            disabled={!pagination.hasNextPage || isLoading}
                            onClick={() =>
                              handlePageChange(pagination.currentPage + 1)
                            }
                          >
                            Next
                          </Button>
                        </div>
                      )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
    </>
  );
};

export default ReportsPage;
